export const UNIT = 'kWh'

/** Longest period the API accepts. */
export const MAX_RANGE_DAYS = 3660

/** Axis label: 12,345 -> "12.3k" */
export function compact(value: number): string {
  return Math.abs(value) >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${Math.round(value)}`
}

export function toIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Calendar-day arithmetic that is immune to DST (works at noon local time). */
export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d, 12)
  date.setDate(date.getDate() + days)
  return toIso(date)
}

export function daysBetween(startIso: string, endIso: string): number {
  const [ys, ms, ds] = startIso.split('-').map(Number)
  const [ye, me, de] = endIso.split('-').map(Number)
  const a = new Date(ys, ms - 1, ds, 12).getTime()
  const b = new Date(ye, me - 1, de, 12).getTime()
  return Math.round((b - a) / 86400000) + 1
}

/**
 * Regional variant used for dates and numbers. The application's "en" is British English
 * (7 Mar 2026, not Mar 7, 2026).
 */
const REGION: Record<string, string> = { en: 'en-GB', es: 'es-ES', ca: 'ca-ES' }

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

/** A UTC date for a "YYYY-MM-DD" string: no timezone can shift the calendar day. */
function utcDate(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(Date.UTC(y, m - 1, d))
}

/**
 * Locale-aware formatters. Month and weekday names come from Intl, not from hand-written English
 * lists, so they follow the language selected in the application.
 */
export function makeFormat(locale: string) {
  const tag = REGION[locale] || locale
  const numbers = new Map<number, Intl.NumberFormat>()
  const numberFormat = (digits: number) => {
    let f = numbers.get(digits)
    if (!f) {
      f = new Intl.NumberFormat(tag, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
      numbers.set(digits, f)
    }
    return f
  }
  const percentFormat = new Intl.NumberFormat(tag, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  })
  const dateFormat = new Intl.DateTimeFormat(tag, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
  const shortDateFormat = new Intl.DateTimeFormat(tag, {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  })
  const weekdayShort = new Intl.DateTimeFormat(tag, { weekday: 'short', timeZone: 'UTC' })
  const weekdayLong = new Intl.DateTimeFormat(tag, { weekday: 'long', timeZone: 'UTC' })
  const monthShort = new Intl.DateTimeFormat(tag, { month: 'short', timeZone: 'UTC' })
  const monthLong = new Intl.DateTimeFormat(tag, { month: 'long', timeZone: 'UTC' })

  const number = (value: number | null | undefined, digits = 0): string =>
    value === null || value === undefined || Number.isNaN(value)
      ? '—'
      : numberFormat(digits).format(value)

  return {
    locale,
    number,
    kwh: (value: number | null | undefined, digits = 0): string =>
      value === null || value === undefined ? '—' : `${number(value, digits)} ${UNIT}`,
    /** 25 -> "+25.0%" (en) / "+25,0 %" (es); 0 and null have no sign */
    percent: (value: number | null | undefined): string =>
      value === null || value === undefined ? '—' : percentFormat.format(value / 100),
    /** "2026-03-07" -> "7 Mar 2026" */
    date: (iso: string | null | undefined): string => {
      const d = iso ? utcDate(iso) : null
      return d ? dateFormat.format(d) : iso || '—'
    },
    /** "2026-03-07" -> "7 Mar 26": short enough for chart axis labels */
    shortDate: (iso: string | null | undefined): string => {
      const d = iso ? utcDate(iso) : null
      return d ? shortDateFormat.format(d) : iso || '—'
    },
    /** weekday name of a calendar date ("2026-03-07" -> "Saturday") */
    weekdayOf: (iso: string): string => {
      const d = utcDate(iso)
      return d ? capitalize(weekdayLong.format(d)) : iso
    },
    /** ISO weekday 1..7 (Monday..Sunday) */
    weekdayShort: (n: number): string =>
      capitalize(weekdayShort.format(new Date(Date.UTC(2024, 0, n)))),
    weekdayLong: (n: number): string =>
      capitalize(weekdayLong.format(new Date(Date.UTC(2024, 0, n)))),
    /** month 1..12 */
    monthShort: (n: number): string =>
      capitalize(monthShort.format(new Date(Date.UTC(2024, n - 1, 1)))),
    monthLong: (n: number): string =>
      capitalize(monthLong.format(new Date(Date.UTC(2024, n - 1, 1)))),
  }
}

export type Fmt = ReturnType<typeof makeFormat>
